import { GENEROS_TREE } from '../constants/musicConstants.js';
import { distanciaKm } from '../lib/geocodingService.js';

const MAIN_GENRE_KEYS = new Set(Object.keys(GENEROS_TREE));

export const getGeneracion = y => {
  if (y < 1946) return 'silenciosa';
  if (y < 1965) return 'boomers';
  if (y < 1981) return 'genx';
  if (y < 1997) return 'millennials';
  if (y < 2013) return 'genz';
  return 'alpha';
};

export const getDecada = y => {
  if (y >= 1960 && y < 1970) return '60s';
  if (y >= 1970 && y < 1980) return '70s';
  if (y >= 1980 && y < 1990) return '80s';
  if (y >= 1990 && y < 2000) return '90s';
  if (y >= 2000 && y < 2010) return '2000s';
  if (y >= 2010 && y < 2020) return '2010s';
  return '2020s';
};

export const itemMatchesGeneralQuery = (item, rawQuery) => {
  const q = String(rawQuery ?? '').trim().toLowerCase();
  if (!q) return true;
  const tokens = q.split(/\s+/).filter(Boolean);
  const parts = [
    item.title,
    item.artist,
    item.seller,
    item.genre,
    item.location,
    item.pais,
    item.ciudad,
    item.localidad,
    item.type,
    item.condition,
    item.label,
    item.year != null && String(item.year),
    item.price != null && String(item.price),
    item.currentBid != null && String(item.currentBid),
  ];
  const blob = parts.filter(x => x != null && x !== '').map(String).join(' ').toLowerCase();
  return tokens.every(t => blob.includes(t));
};

function normUb(v) {
  return String(v ?? '')
    .trim()
    .toLowerCase();
}

function eqUb(val, target) {
  return normUb(val) === normUb(target);
}

/** Coincide género del ítem con la selección: subgéneros exactos o categoría principal (= todo su árbol). */
export function itemMatchesGeneroFilter(itemGenre, generosSel) {
  if (!generosSel || generosSel.length === 0) return true;
  const g = itemGenre == null ? '' : String(itemGenre);
  return generosSel.some(sel => {
    if (MAIN_GENRE_KEYS.has(sel)) {
      const subs = GENEROS_TREE[sel];
      return (subs && subs.includes(g)) || g === sel;
    }
    return g === sel;
  });
}

/**
 * Filtrado por ubicación (provincia / ciudad → municipio / localidad) o todo (vacío).
 * `ubicacionSelector`: { provincia, municipio } (nombres INE donde aplica).
 */
export const itemMatchesUbicacionFilter = (item, ubicacionSelector) => {
  if (!ubicacionSelector || typeof ubicacionSelector !== 'object') return true;
  const provincia = String(ubicacionSelector.provincia ?? '').trim();
  const municipio = String(ubicacionSelector.municipio ?? '').trim();

  if (municipio) return eqUb(item.localidad, municipio);
  if (provincia) return eqUb(item.ciudad, provincia);

  return true;
};

/** `geoFilter`: { userLat, userLng, distanciaKm } con números finitos — si falta coords en el ítem se excluye. */
export function itemMatchesGeoFilter(item, geoFilter) {
  if (!geoFilter || typeof geoFilter !== 'object') return true;
  const uLat = Number(geoFilter.userLat);
  const uLng = Number(geoFilter.userLng);
  const maxKm = Number(geoFilter.distanciaKm);
  if (!Number.isFinite(uLat) || !Number.isFinite(uLng) || !(maxKm > 0)) return true;
  const ilat = item.lat;
  const ilng = item.lng;
  if (!Number.isFinite(Number(ilat)) || !Number.isFinite(Number(ilng))) return false;
  return distanciaKm(uLat, uLng, ilat, ilng) <= maxKm;
}

const CONDITION_ORDER = { M: 0, 'VG+': 1, VG: 2, 'G+': 3, G: 4, P: 5 };

const sortPriceVal = i => {
  if (i.isAuction && i.currentBid != null && !Number.isNaN(Number(i.currentBid))) return Number(i.currentBid);
  return Number(i.price) || 0;
};

const partnerBoostScore = i =>
  Array.isArray(i?.sellerPartnerRoleKeys) && i.sellerPartnerRoleKeys.length > 0 ? 1 : 0;

export const sortMarketplaceItems = (items, ordenar) => {
  return [...items].sort((a, b) => {
    if (ordenar === 'precio_asc') return sortPriceVal(a) - sortPriceVal(b);
    if (ordenar === 'precio_desc') return sortPriceVal(b) - sortPriceVal(a);
    if (ordenar === 'artista') return a.artist.localeCompare(b.artist);
    if (ordenar === 'titulo') return a.title.localeCompare(b.title);
    if (ordenar === 'anio_asc') return a.year - b.year;
    if (ordenar === 'anio_desc') return b.year - a.year;
    if (ordenar === 'estado') return (CONDITION_ORDER[a.condition] || 9) - (CONDITION_ORDER[b.condition] || 9);
    if (ordenar === 'recomendaciones') {
      const pa = partnerBoostScore(a);
      const pb = partnerBoostScore(b);
      if (pa !== pb) return pb - pa;
    }
    if (ordenar === 'recientes' || ordenar === 'recomendaciones') {
      if (a.createdAt && b.createdAt) return new Date(b.createdAt) - new Date(a.createdAt);
      return 0;
    }
    // Fallback: más recientes primero
    if (a.createdAt && b.createdAt) return new Date(b.createdAt) - new Date(a.createdAt);
    return 0;
  });
};

/**
 * Mismas reglas que el buscador del mercado sobre un array de ítems (catálogo o solo subastas).
 * `f` incluye los campos de filtro; `favoritesOnlyMode` / `likes` solo para el listado principal con favoritos.
 */
export function filterInventoryByMarketplaceRules(
  items,
  f,
  { favoritesOnlyMode = false, likes = {}, geoFilter = null } = {}
) {
  const {
    filtrosAplicados,
    tiposSel,
    condFiltros,
    generosSel,
    genFiltros,
    decadaFiltros,
    precioRange,
    busqueda,
    ubicacionSelector,
    ordenar,
  } = f;
  const base = sortMarketplaceItems(
    (filtrosAplicados
      ? items.filter(item => {
          if (tiposSel.length && !tiposSel.includes(item.type)) return false;
          if (condFiltros.length && !condFiltros.includes(item.condition)) return false;
          if (generosSel.length && !itemMatchesGeneroFilter(item.genre, generosSel)) return false;
          if (genFiltros.length && !genFiltros.includes(getGeneracion(item.year))) return false;
          if (decadaFiltros.length && !decadaFiltros.includes(getDecada(item.year))) return false;
          if (item.price < precioRange[0] || item.price > precioRange[1]) return false;
          return true;
        })
      : items)
      .filter(item => itemMatchesGeneralQuery(item, busqueda))
      .filter(item => itemMatchesUbicacionFilter(item, ubicacionSelector))
      .filter(item => itemMatchesGeoFilter(item, geoFilter)),
    ordenar
  );
  if (!favoritesOnlyMode) return base;
  return base.filter(item => likes[item.id]);
}
